// BasePanel.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import baseStyles from './BasePanel.module.css';
import { AD_TYPES } from '../../../../constants/enums';
import PdfService from '../../../../services/pdfService';

// Minimum gap enforced between two automatically-triggered (timer/video-ended) advances; see tryAutoAdvance
const MIN_MS_BETWEEN_AUTO_ADVANCES = 250;

const loadPdfJs = async () => {
    if (window.pdfjsLib) return window.pdfjsLib;

    // Load PDF.js library
    const pdfjsScript = document.createElement('script');
    pdfjsScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';

    await new Promise((resolve) => {
        pdfjsScript.onload = resolve;
        document.head.appendChild(pdfjsScript);
    });

    // Load worker
    window.pdfjsLib = window['pdfjs-dist/build/pdf'];
    window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

    return window.pdfjsLib;
};

// PDF viewer component that also deals with cross-platform compatibility
const PDFViewer = ({ src, onError, className }) => {
    const canvasRef = useRef(null);
    const [isLoading, setIsLoading] = useState(true);
    const [pdfDoc, setPdfDoc] = useState(null);
    const renderTask = useRef(null);
    const prevSrcRef = useRef(src);
    const onErrorRef = useRef(onError);

    const filename = src.split('/').pop();

    useEffect(() => {
        onErrorRef.current = onError;
    }, [onError]);

    useEffect(() => {
        const renderPdf = async () => {
            if (!pdfDoc || !canvasRef.current) {
                return;
            }

            try {
                if (renderTask.current) {
                    renderTask.current.cancel();
                }

                const page = await pdfDoc.getPage(1);
                const canvas = canvasRef.current;
                const context = canvas.getContext('2d');

                const viewport = page.getViewport({ scale: 1.5 });

                canvas.height = viewport.height;
                canvas.width = viewport.width;

                renderTask.current = page.render({
                    canvasContext: context,
                    viewport: viewport
                });

                await renderTask.current.promise;
                setIsLoading(false);
            } catch (error) {
                console.error('Error rendering PDF:', error);
                if (error.name !== 'RenderingCancelledException') {
                    onErrorRef.current();
                    setIsLoading(false);
                }
            }
        };

        renderPdf();

        return () => {
            if (renderTask.current) {
                renderTask.current.cancel();
            }
        };
    }, [pdfDoc]);

    useEffect(() => {
        let mounted = true;

        // Check if src has changed
        if (prevSrcRef.current === src && pdfDoc) {
            return;
        }

        prevSrcRef.current = src;
        setIsLoading(true);

        const loadPDF = async () => {
            try {
                if (renderTask.current) {
                    renderTask.current.cancel();
                }

                const pdfjsLib = await loadPdfJs();
                const arrayBuffer = await PdfService.getPdf(filename);

                const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
                const pdf = await loadingTask.promise;

                if (mounted) {
                    setPdfDoc(pdf);
                }
            } catch (error) {
                console.error('Error loading PDF:', error);
                if (mounted) {
                    onErrorRef.current();
                    setIsLoading(false);
                }
            }
        };

        loadPDF();

        return () => {
            mounted = false;
        };
    }, [filename, src, pdfDoc]);

    return (
        <div className={className}>
            <canvas ref={canvasRef} className={baseStyles.pdfCanvas} />
            {isLoading && (
                <div className={baseStyles.spinnerContainer}>
                    <div className={baseStyles.spinner} />
                </div>
            )}
        </div>
    );
};

const BasePanel = ({
    mediaArray,
    backgroundImage,
    cannotLoadImage,
    numberOfFrames = 1,
    customStyles = {},
    autoPlayInterval = 10000,
    videoAutoPlayInterval = 10000
}) => {
    const [currentIndices, setCurrentIndices] = useState(Array(numberOfFrames).fill(0));
    const [errorLoadingMedia, setErrorLoadingMedia] = useState(Array(numberOfFrames).fill(false));
    const [mediaError, setMediaError] = useState(Array(numberOfFrames).fill(false));
    const mediaRefs = useRef(Array(numberOfFrames).fill(null));
    const timerRef = useRef(null);
    // All frames always share the same group index (see setCurrentIndices callers below); kept in a ref
    // so the timer logic always reads the live index instead of a stale closure over React state.
    const currentIndexRef = useRef(0);
    // Guards only the two *automatic* triggers (interval timer vs. video 'ended') from double-advancing
    // when both become due within the same instant (e.g. videoAutoPlayInterval close to the video's real
    // duration); manual next/previous navigation is intentionally exempt from this guard.
    const lastAutoAdvanceAtRef = useRef(0);

    // Group media by groupingKey
    const groupedMedia = React.useMemo(() => {
        return mediaArray.reduce((acc, media) => {
            if (!acc[media.groupingKey]) {
                acc[media.groupingKey] = [];
            }
            acc[media.groupingKey][media.groupingIndex] = media;
            return acc;
        }, {});
    }, [mediaArray]);

    // Merge base styles with custom styles
    const mergedStyles = Object.keys(baseStyles).reduce((acc, key) => {
        acc[key] = `${baseStyles[key]} ${customStyles[key] || ''}`;
        return acc;
    }, {});

    const goToNextGroup = useCallback(() => {
        const groupCount = Object.keys(groupedMedia).length;
        if (groupCount <= 1) return;

        currentIndexRef.current = (currentIndexRef.current + 1) % groupCount;
        setCurrentIndices(Array(numberOfFrames).fill(currentIndexRef.current));
    }, [groupedMedia, numberOfFrames]);

    const goToPreviousGroup = useCallback(() => {
        const groupCount = Object.keys(groupedMedia).length;
        if (groupCount <= 1) return;

        currentIndexRef.current = (currentIndexRef.current - 1 + groupCount) % groupCount;
        setCurrentIndices(Array(numberOfFrames).fill(currentIndexRef.current));
    }, [groupedMedia, numberOfFrames]);

    // Coalesces the interval timer and the video 'ended' event into at most one advance when both fire together
    const tryAutoAdvance = useCallback(() => {
        const now = Date.now();
        if (now - lastAutoAdvanceAtRef.current < MIN_MS_BETWEEN_AUTO_ADVANCES) return;
        lastAutoAdvanceAtRef.current = now;
        goToNextGroup();
    }, [goToNextGroup]);

    // Video's own 'ended' event always wins, but while it's playing a null videoAutoPlayInterval means no timer at all
    const currentGroupHasVideo = useCallback(() => {
        const groupKeys = Object.keys(groupedMedia);
        const currentGroupMedia = groupedMedia[groupKeys[currentIndexRef.current]] || [];
        return currentGroupMedia.some(media => media?.type === AD_TYPES.VIDEO);
    }, [groupedMedia]);

    const startAutoPlayTimer = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }

        // Only start timer if we have multiple items
        if (Object.keys(groupedMedia).length <= 1) return;

        const interval = currentGroupHasVideo() ? videoAutoPlayInterval : autoPlayInterval;
        if (interval === null || interval === undefined) return;

        timerRef.current = setTimeout(() => {
            tryAutoAdvance();
            startAutoPlayTimer(); // Restart timer after switching
        }, interval);
    }, [groupedMedia, autoPlayInterval, videoAutoPlayInterval, currentGroupHasVideo, tryAutoAdvance]);

    const handleNext = useCallback(() => {
        if (Object.keys(groupedMedia).length <= 1) return;

        setErrorLoadingMedia(prev => prev.map(() => false));
        setMediaError(prev => prev.map(() => false));
        goToNextGroup();

        // Reset timer after manual navigation
        startAutoPlayTimer();
    }, [groupedMedia, goToNextGroup, startAutoPlayTimer]);

    const handlePrevious = useCallback(() => {
        if (Object.keys(groupedMedia).length <= 1) return;

        setErrorLoadingMedia(prev => prev.map(() => false));
        setMediaError(prev => prev.map(() => false));
        goToPreviousGroup();

        // Reset timer after manual navigation
        startAutoPlayTimer();
    }, [groupedMedia, goToPreviousGroup, startAutoPlayTimer]);

    const handleVideoEnded = useCallback(() => {
        if (Object.keys(groupedMedia).length <= 1) return;

        setErrorLoadingMedia(prev => prev.map(() => false));
        setMediaError(prev => prev.map(() => false));
        tryAutoAdvance();

        // Reset timer after the video-driven switch
        startAutoPlayTimer();
    }, [groupedMedia, tryAutoAdvance, startAutoPlayTimer]);

    const playMedia = useCallback((frameIndex) => {
        if (mediaRefs.current[frameIndex] && mediaRefs.current[frameIndex].play) {
            setMediaError(prev => {
                const newErrors = [...prev];
                newErrors[frameIndex] = false;
                return newErrors;
            });
            mediaRefs.current[frameIndex].load();

            const playAttempt = () => {
                mediaRefs.current[frameIndex].play().catch(error => {
                    if (error.name === 'AbortError') {
                        setTimeout(playAttempt, 100);
                    } else {
                        console.error("Error playing media:", error);
                        setMediaError(prev => {
                            const newErrors = [...prev];
                            newErrors[frameIndex] = true;
                            return newErrors;
                        });
                    }
                });
            };

            if (mediaRefs.current[frameIndex].readyState >= 2) {
                playAttempt();
            } else {
                mediaRefs.current[frameIndex].addEventListener('canplay', playAttempt, { once: true });
            }
        }
    }, []);

    // Handle video looping
    useEffect(() => {
        mediaRefs.current.forEach((ref) => {
            if (ref && ref.loop !== undefined) {
                ref.loop = (Object.keys(groupedMedia).length === 1);
            }
        });
    }, [groupedMedia]);

    // Handle media playback and timer
    useEffect(() => {
        // Start initial timer
        startAutoPlayTimer();

        // Handle video playback
        currentIndices.forEach((currentIndex, frameIndex) => {
            const panelIndex = frameIndex + 1;
            const groupKeys = Object.keys(groupedMedia);
            const currentGroupKey = groupKeys[currentIndex];
            const currentMedia = groupedMedia[currentGroupKey]?.[panelIndex];

            if (currentMedia?.type === AD_TYPES.VIDEO) {
                playMedia(panelIndex);
            }
        });

        // Cleanup
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }

            // Only clean up video refs
            mediaRefs.current.forEach((ref, index) => {
                const currentIndex = currentIndices[index - 1];
                const groupKeys = Object.keys(groupedMedia);
                const currentGroupKey = groupKeys[currentIndex];
                const currentMedia = groupedMedia[currentGroupKey]?.[index];

                if (ref && currentMedia?.type === AD_TYPES.VIDEO) {
                    if (ref.pause) ref.pause();
                    if (ref.removeAttribute) ref.removeAttribute('src');
                    if (ref.load) ref.load();
                }
            });
        };
    }, [currentIndices, groupedMedia, playMedia, startAutoPlayTimer]);

    const renderMedia = (frameIndex) => {
        const groupKeys = Object.keys(groupedMedia);
        const currentGroupKey = groupKeys[currentIndices[frameIndex - 1]];
        const currentMedia = groupedMedia[currentGroupKey]?.[frameIndex];

        if (!currentMedia) return null;

        if (mediaError[frameIndex]) {
            return (
                <img
                    src={cannotLoadImage}
                    alt={currentMedia.alt || 'Unable to load media'}
                    className={mergedStyles.overlayImage}
                    style={{
                        aspectRatio: `${currentMedia.width}/${currentMedia.height}`
                    }}
                />
            );
        }

        switch (currentMedia.type) {
            case AD_TYPES.IMAGE:
                return (
                    <img
                        src={errorLoadingMedia[frameIndex]
                            ? cannotLoadImage
                            : currentMedia.src.trim() === ''
                                ? cannotLoadImage
                                : currentMedia.src}
                        alt={currentMedia.alt || 'Slideshow Image'}
                        onError={() => setErrorLoadingMedia(prev => {
                            const newErrors = [...prev];
                            newErrors[frameIndex] = true;
                            return newErrors;
                        })}
                        className={mergedStyles.overlayImage}
                        style={{
                            aspectRatio: `${currentMedia.width}/${currentMedia.height}`
                        }}
                    />
                );
            case AD_TYPES.VIDEO:
                return (
                    <video
                        ref={el => mediaRefs.current[frameIndex] = el}
                        className={mergedStyles.overlayVideo}
                        muted
                        onEnded={handleVideoEnded}
                        onError={() => setMediaError(prev => {
                            const newErrors = [...prev];
                            newErrors[frameIndex] = true;
                            return newErrors;
                        })}
                        poster={cannotLoadImage}
                        style={{
                            aspectRatio: `${currentMedia.width}/${currentMedia.height}`
                        }}
                    >
                        <source src={currentMedia.src} type="video/mp4" />
                        Your browser does not support the video tag.
                    </video>
                );
            case AD_TYPES.PDF:
                return (
                    <PDFViewer
                        src={currentMedia.src}
                        onError={() => setMediaError(prev => {
                            const newErrors = [...prev];
                            newErrors[frameIndex] = true;
                            return newErrors;
                        })}
                        className={mergedStyles.overlayImage}
                    />
                );
            default:
                return null;
        }
    };

    const renderNavigationButtons = (frameIndex) => {
        if (Object.keys(groupedMedia).length <= 1) return null;

        return (
            <React.Fragment>
                {frameIndex === 0 && (
                    <button
                        className={mergedStyles.previousButton}
                        onClick={handlePrevious}
                    >
                        &#10094;
                    </button>
                )}
                {frameIndex === numberOfFrames - 1 && (
                    <button
                        className={mergedStyles.nextButton}
                        onClick={handleNext}
                    >
                        &#10095;
                    </button>
                )}
            </React.Fragment>
        );
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'row', gap: '10px' }}>
            {Array.from({ length: numberOfFrames }).map((_, frameIndex) => (
                <div key={frameIndex} className={mergedStyles.container}>
                    {backgroundImage && (
                        <img
                            src={backgroundImage}
                            alt="Background"
                            className={mergedStyles.backgroundImage}
                        />
                    )}
                    {renderMedia(frameIndex + 1)}
                    {renderNavigationButtons(frameIndex)}
                </div>
            ))}
        </div>
    );
};

export default BasePanel;